import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PartnerManualService } from '../services/partner-manual.service';

interface TocEntry {
  id: string;
  label: string;
}

const HIGHLIGHT_CLASS = 'pp-manual-search-hit';

// Wraps the first occurrence of `needle` within `node`'s text, then
// recurses into the remainder (the wrap's nextSibling) rather than
// re-scanning the original node — surroundContents() splits the text node,
// so any offset computed against the original node is invalid afterward.
function wrapMatches(doc: Document, node: Text, needle: string, out: HTMLElement[]): void {
  const text = node.textContent || '';
  const idx = text.toLowerCase().indexOf(needle);
  if (idx === -1) return;

  const range = doc.createRange();
  range.setStart(node, idx);
  range.setEnd(node, idx + needle.length);

  const mark = doc.createElement('mark');
  mark.className = HIGHLIGHT_CLASS;
  mark.style.background = '#ffe066';
  mark.style.color = '#000';
  range.surroundContents(mark);
  out.push(mark);

  const remainder = mark.nextSibling;
  if (remainder && remainder.nodeType === Node.TEXT_NODE) {
    wrapMatches(doc, remainder as Text, needle, out);
  }
}

@Component({
  selector: 'app-manual',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './manual.component.html',
  styleUrl: './manual.component.scss',
})
export class ManualComponent implements OnInit {
  @ViewChild('manualFrame') manualFrame!: ElementRef<HTMLIFrameElement>;

  safeHtml: SafeHtml | null = null;
  loading = true;
  loadError = false;

  toc: TocEntry[] = [];

  searchTerm = '';
  matches: HTMLElement[] = [];
  currentMatchIndex = -1;

  constructor(
    private partnerManualService: PartnerManualService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.partnerManualService.getManual().subscribe({
      next: (html) => {
        this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(html);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  // Fires once the srcdoc content finishes parsing, and again any time
  // safeHtml is reassigned. This is the only point of contact with the
  // iframe's own document — everything below reads/writes through it.
  onIframeLoad(): void {
    const doc = this.manualFrame?.nativeElement?.contentDocument;
    if (!doc) return;

    this.toc = Array.from(doc.querySelectorAll('section[id]')).map((section) => {
      const heading = section.querySelector('h2');
      const label = (heading?.textContent || section.id).replace(/^\d+/, '').trim();
      return { id: section.id, label };
    });

    // A stale match list from a previous load would point at detached DOM
    // nodes and break prev/next — reset on every (re)load.
    this.matches = [];
    this.currentMatchIndex = -1;
  }

  jumpTo(id: string): void {
    const doc = this.manualFrame?.nativeElement?.contentDocument;
    doc?.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  onSearchChange(): void {
    this.clearHighlights();

    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return;

    const doc = this.manualFrame?.nativeElement?.contentDocument;
    if (!doc) return;

    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parentTag = node.parentElement?.tagName;
        if (parentTag === 'SCRIPT' || parentTag === 'STYLE' || parentTag === 'MARK') {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    // Collect nodes into a static list first — wrapping matches while the
    // TreeWalker is still live would desync it, since surroundContents()
    // splits the text node it's walking.
    const textNodes: Text[] = [];
    let current: Node | null;
    while ((current = walker.nextNode())) {
      textNodes.push(current as Text);
    }

    for (const node of textNodes) {
      wrapMatches(doc, node, term, this.matches);
    }

    if (this.matches.length) {
      this.currentMatchIndex = 0;
      this.focusCurrentMatch();
    }
  }

  nextMatch(): void {
    this.step(1);
  }

  prevMatch(): void {
    this.step(-1);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.clearHighlights();
  }

  private step(delta: number): void {
    if (!this.matches.length) return;
    this.currentMatchIndex = (this.currentMatchIndex + delta + this.matches.length) % this.matches.length;
    this.focusCurrentMatch();
  }

  private focusCurrentMatch(): void {
    this.matches.forEach((mark, i) => {
      const active = i === this.currentMatchIndex;
      mark.style.outline = active ? '2px solid #f59f00' : 'none';
    });
    this.matches[this.currentMatchIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private clearHighlights(): void {
    const doc = this.manualFrame?.nativeElement?.contentDocument;
    if (doc) {
      doc.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`).forEach((mark) => {
        const parent = mark.parentNode;
        if (!parent) return;
        parent.replaceChild(doc.createTextNode(mark.textContent || ''), mark);
        parent.normalize();
      });
    }
    this.matches = [];
    this.currentMatchIndex = -1;
  }
}
